import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CHROLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      toast.error("Please enter email and password");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/login`, { 
        email: email.toLowerCase().trim(), 
        password 
      });
      
      const { access_token, user } = response.data;
      
      localStorage.setItem("authToken", access_token);
      localStorage.setItem("chroToken", access_token);
      localStorage.setItem("userRole", user.role);
      localStorage.setItem("userInfo", JSON.stringify(user));
      
      toast.success(`Welcome, ${user.fullName}!`);
      navigate("/chro/dashboard");
      
    } catch (err) {
      console.error("Login error:", err);
      const message = err.response?.data?.detail || "Invalid email or password";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden" data-testid="chro-login-page">
      {/* Background with Sagicor gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #00AEEF 0%, #00a0dc 30%, #4db8e8 50%, #63C465 100%)'
        }}
      />
      
      {/* Geometric shapes - diagonal stripes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute -bottom-20 -left-20 w-80 h-80"
          style={{
            background: 'linear-gradient(45deg, rgba(78, 205, 196, 0.6) 0%, rgba(78, 205, 196, 0.6) 33%, rgba(0, 174, 239, 0.5) 33%, rgba(0, 174, 239, 0.5) 66%, rgba(99, 196, 101, 0.4) 66%)',
            transform: 'rotate(-15deg)',
          }}
        />
        <div 
          className="absolute -top-10 -right-20 w-96 h-20"
          style={{
            background: 'rgba(99, 102, 241, 0.5)',
            transform: 'rotate(-15deg)',
          }}
        />
        <div 
          className="absolute top-1/3 -right-10 w-64 h-12"
          style={{
            background: 'rgba(99, 102, 241, 0.4)',
            transform: 'rotate(-15deg)',
          }}
        />
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="chro-dotted-lines" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="1" strokeDasharray="4,8" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#chro-dotted-lines)" />
        </svg>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div 
          className="bg-white rounded-2xl shadow-2xl p-8"
          style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
        >
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-[#3b3b3b]">
              Welcome! Sign in to your account
            </h1>
            <p className="text-xs text-[#9ca3af] font-light mt-1">
              Sagicor Technology Assessment Portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#3b3b3b]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-white border-[#d1d5db] focus:border-[#00AEEF] focus:ring-[#00AEEF]/20 rounded-lg"
                autoComplete="email"
                data-testid="chro-email"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-[#3b3b3b]">
                  Password
                </Label>
                <a 
                  href="#" 
                  className="text-sm text-[#00AEEF] hover:text-[#00AEEF]/80 font-medium"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.info("Please contact supportsagicor@thcohq.com for password reset");
                  }}
                >
                  Forgot your password?
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 bg-white border-[#d1d5db] focus:border-[#00AEEF] focus:ring-[#00AEEF]/20 rounded-lg pr-12"
                  autoComplete="current-password"
                  data-testid="chro-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#6b7280] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#00AEEF] hover:bg-[#0099d6] text-white font-semibold text-base rounded-lg shadow-lg transition-all"
              data-testid="chro-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#6b7280]">
              Need help. Contact{" "}
              <a href="mailto:supportsagicor@thcohq.com" className="text-[#00AEEF] hover:text-[#00AEEF]/80 font-medium">
                supportsagicor@thcohq.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 mt-12 text-center">
        <p className="text-sm text-white/70">© 2026 THCO</p>
      </div>
    </div>
  );
}
